import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Camera, Link2, X, Search, Upload, Wallet, AlertTriangle, Pencil, ListFilter, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { BajaDialog } from "@/components/app/HistorialBajas";
import { UserMinus } from "lucide-react";
import { PLAN_KEYS, PLAN_LABELS as PLAN_LABELS_MAP, planAmount, hasAnyPlans } from "@/lib/plans";

// Normaliza texto: minúsculas, sin acentos, sin ordinales (º °), separa dígito-letra ("1a"->"1 a").
const normStr = (v) => (v == null ? "" : String(v))
  .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .replace(/(\d)([a-z])/g, "$1 $2").replace(/([a-z])(\d)/g, "$1 $2")
  .replace(/\s+/g, " ").trim();

// Extrae grado (número) y grupo (letra) del texto libre del campo "grado" (ej. "1°A" -> {grado:"1", grupo:"A"}).
const parseGradeGroup = (grade) => {
  const n = normStr(grade);
  const numM = n.match(/\d+/);
  const grado = numM ? numM[0] : "";
  const grupo = (n.split(" ").find((w) => /^[a-z]$/.test(w)) || "").toUpperCase();
  return { grado, grupo };
};

const RELATIONS = ["Mamá", "Papá", "Tutor(a)", "Abuelo(a)", "Otro"];
const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const PLAN_LABELS = PLAN_LABELS_MAP;
// Etiqueta "Nombre (parentesco)" del primer tutor de un alumno.
function primaryTutorLabel(person) {
  const tutors = person?.billing?.tutors || [];
  const t = tutors.find((x) => x && (x.name || "").trim());
  if (!t) return "";
  const rel = (t.relation || "").trim();
  return rel ? `${t.name} (${rel.toLowerCase()})` : t.name;
}

function PersonPhoto({ person }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let blobUrl;
    let cancelled = false;
    (async () => {
      if (!person.photo_path) return;
      try {
        const r = await api.get(`/files/${person.photo_path}`, { responseType: "blob" });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [person.photo_path]);

  if (!src) {
    return (
      <div className="w-12 h-12 rounded-full bg-nardo-100 flex items-center justify-center text-navy font-display font-bold text-sm border border-nardo-200">
        {person.full_name?.[0]?.toUpperCase() || "?"}
      </div>
    );
  }
  return <img src={src} alt="" className="w-12 h-12 rounded-full object-cover border border-nardo-200" />;
}

export default function PeopleManager({ kind = "student" }) {
  const { user } = useAuth();
  const isStaff = user?.role === "staff" || user?.is_staff;
  const [bajaItem, setBajaItem] = useState(null);
  const isTeacher = kind === "teacher";
  const labels = isTeacher
    ? { title: "Docentes y Personal", section: "Personal", add: "Agregar docente / personal", newTitle: "Nuevo docente o personal", gradeLabel: "Materia / Cargo", gradePh: "Matemáticas · Coordinador", empty: "Aún no hay docentes ni personal. Agrega el primero." }
    : { title: "Alumnos", section: "Personas", add: "Agregar alumno", newTitle: "Nuevo alumno", gradeLabel: "Grado", gradePh: "6° A", empty: "Aún no hay alumnos. Agrega el primero." };

  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [fLevel, setFLevel] = useState("");
  const [fGrade, setFGrade] = useState("");
  const [fGroup, setFGroup] = useState("");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [grade, setGrade] = useState("");
  const [level, setLevel] = useState("");
  const [uid, setUid] = useState("");
  const shifts = user?.shifts || [];
  const [turno, setTurno] = useState("");
  const [jornadas, setJornadas] = useState([]);
  const [jornada, setJornada] = useState("");
  const [editJornada, setEditJornada] = useState("");
  const [tutors, setTutors] = useState([{ name: "", whatsapp: "", email: "", relation: "" }]);
  const updateTutor = (i, k, v) => setTutors((ts) => ts.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const addTutor = () => setTutors((ts) => [...ts, { name: "", whatsapp: "", email: "", relation: "" }]);
  const removeTutor = (i) => setTutors((ts) => (ts.length > 1 ? ts.filter((_, idx) => idx !== i) : ts));
  const [plan, setPlan] = useState("");
  const [blood, setBlood] = useState("");
  const [monthlyFee, setMonthlyFee] = useState("");
  const [enrollment, setEnrollment] = useState("");
  const [schId, setSchId] = useState("");
  const scholarships = user?.scholarships || [];
  const hasPlans = hasAnyPlans(user);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [quota, setQuota] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editUid, setEditUid] = useState("");
  const [editTurno, setEditTurno] = useState("");
  const [editPlan, setEditPlan] = useState("");
  const [editMonthly, setEditMonthly] = useState("");
  const [editEnroll, setEditEnroll] = useState("");
  const [editSchId, setEditSchId] = useState("");
  useEffect(() => {
    setEditTurno(editItem?.turno || "");
    setEditJornada(editItem?.jornada || "");
    const b = editItem?.billing || {};
    setEditPlan(b.plan || "");
    setEditMonthly(b.monthly_fee != null ? String(b.monthly_fee) : "");
    setEditEnroll(b.enrollment_fee != null ? String(b.enrollment_fee) : "");
    const sch = (user?.scholarships || []).find((s) => (b.scholarship_name || "") && (s.name || "") === (b.scholarship_name || ""));
    setEditSchId(sch ? sch.id : "");
  }, [editItem]); // eslint-disable-line react-hooks/exhaustive-deps
  const fileInputs = useRef({});
  const importInput = useRef(null);

  const LEVELS = ["Preescolar", "Primaria baja", "Primaria alta", "Primaria", "Secundaria", "Preparatoria"];

  const load = async () => {
    const { data } = await api.get("/students", { params: { kind } });
    setItems(data);
    try { const q = await api.get("/registration-quota"); setQuota(q.data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isTeacher) return;
    api.get("/direccion/jornadas").then(({ data }) => setJornadas(data.jornadas || [])).catch(() => {});
  }, [isTeacher]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const cleanTutors = tutors
        .map((t) => ({ name: t.name.trim(), whatsapp: t.whatsapp.trim(), email: t.email.trim(), relation: (t.relation || "").trim() }))
        .filter((t) => t.name || t.whatsapp || t.email);
      const sch = scholarships.find((s) => s.id === schId);
      await api.post("/students", {
        full_name: name, grade, level: isTeacher ? null : level, nfc_uid: uid, kind, turno: turno || null,
        jornada: isTeacher ? (jornada || null) : null,
        tutors: isTeacher ? [] : cleanTutors, plan: isTeacher ? "" : plan, blood_type: isTeacher ? "" : blood,
        monthly_fee: isTeacher ? 0 : (Number(monthlyFee) || 0),
        enrollment_fee: isTeacher ? 0 : (Number(enrollment) || 0),
        scholarship_pct: isTeacher ? 0 : (sch ? Number(sch.pct) || 0 : 0),
        scholarship_name: isTeacher ? "" : (sch ? sch.name : ""),
      });
      toast.success(isTeacher ? "Docente agregado" : "Alumno agregado");
      setName(""); setGrade(""); setLevel(""); setUid(""); setTurno(""); setJornada(""); setPlan(""); setBlood(""); setMonthlyFee(""); setEnrollment(""); setSchId(""); setTutors([{ name: "", whatsapp: "", email: "", relation: "" }]); setOpen(false);
      load();
    } catch {
      toast.error("Error al crear");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(isTeacher ? "¿Eliminar?" : "¿Eliminar definitivamente? Si el alumno se va de la escuela usa mejor \"Dar de baja\". De todos modos quedará registro en el Histórico de bajas.")) return;
    await api.delete(`/students/${id}`);
    toast.success("Eliminado");
    load();
  };

  const onPhoto = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/students/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Foto actualizada");
      load();
    } catch {
      toast.error("Error al subir foto");
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/nfc/program/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const onImport = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await api.post("/students/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} ${isTeacher ? "docente(s)" : "alumno(s)"} importado(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar el archivo");
    } finally {
      setImporting(false);
      if (importInput.current) importInput.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const headers = isTeacher ? "Nombre,Cargo,UID NFC" : "Nombre,Grado,Nivel,UID NFC";
    const sample = isTeacher
      ? "Prof. Juan Pérez,Matemáticas,04 A1 9F 22"
      : "María González,6° A,Primaria,04 A1 9F 22";
    const csv = `${headers}\n${sample}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = isTeacher ? "plantilla_docentes.csv" : "plantilla_alumnos.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const tid = (k) => isTeacher ? `teacher-${k}` : `student-${k}`;

  const filteredItems = useMemo(() => {
    const tokens = normStr(query).split(" ").filter(Boolean);
    return items.filter((s) => {
      // Filtros rápidos (Nivel / Grado / Grupo)
      if (fLevel && normStr(s.level) !== normStr(fLevel)) return false;
      if (fGrade || fGroup) {
        const { grado, grupo } = parseGradeGroup(s.grade);
        if (fGrade && grado !== fGrade) return false;
        if (fGroup && grupo !== fGroup) return false;
      }
      if (tokens.length === 0) return true;
      const combined = normStr([s.full_name, s.grade, s.level, s.group, s.group_label, s.nfc_uid, primaryTutorLabel(s)].join(" "));
      const words = combined.split(" ");
      // Cada token debe coincidir (AND). Números y letras sueltas (grupo) exigen palabra exacta;
      // el texto (nombre/nivel) coincide por subcadena.
      return tokens.every((t) =>
        (/^\d+$/.test(t) || t.length === 1) ? words.includes(t) : words.some((w) => w.includes(t))
      );
    });
  }, [items, query, fLevel, fGrade, fGroup]);

  // Opciones para los menús de filtro rápido (derivadas de los alumnos cargados).
  const filterOptions = useMemo(() => {
    const levels = new Set(), grades = new Set(), groups = new Set();
    items.forEach((s) => {
      if (s.level) levels.add(s.level);
      const { grado, grupo } = parseGradeGroup(s.grade);
      if (grado) grades.add(grado);
      if (grupo) groups.add(grupo);
    });
    return {
      levels: Array.from(levels).sort((a, b) => a.localeCompare(b, "es")),
      grades: Array.from(grades).sort((a, b) => Number(a) - Number(b)),
      groups: Array.from(groups).sort(),
    };
  }, [items]);

  const anyFilter = !!(query || fLevel || fGrade || fGroup);
  const clearAll = () => { setQuery(""); setFLevel(""); setFGrade(""); setFGroup(""); };

  const filterSuffix = () => {
    const parts = [];
    if (fGrade) parts.push(`${fGrade}${fGroup ? fGroup : ""}`);
    else if (fGroup) parts.push(`Grupo_${fGroup}`);
    if (fLevel) parts.push(fLevel);
    if (query) parts.push(query);
    return parts.length ? "_" + normStr(parts.join(" ")).replace(/\s+/g, "_") : "";
  };

  const exportRows = () => filteredItems.map((s) => {
    const { grado, grupo } = parseGradeGroup(s.grade);
    return isTeacher
      ? [s.full_name || "", s.grade || "", s.nfc_uid || ""]
      : [s.full_name || "", s.grade || "", grado || "", grupo || "", s.level || "", s.nfc_uid || "", primaryTutorLabel(s) || ""];
  });

  const exportCSV = () => {
    if (filteredItems.length === 0) { toast.error("No hay registros para exportar"); return; }
    const head = isTeacher ? ["Nombre", "Cargo/Materia", "UID NFC"] : ["Nombre", "Grado", "Grado N.", "Grupo", "Nivel", "UID NFC", "Tutor"];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [head, ...exportRows()].map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${isTeacher ? "personal" : "alumnos"}${filterSuffix()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filteredItems.length} registros exportados a Excel (CSV)`);
  };

  const exportPDF = () => {
    if (filteredItems.length === 0) { toast.error("No hay registros para exportar"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const school = user?.school_name || user?.name || "Colegio";
    const subtitleParts = [];
    if (fLevel) subtitleParts.push(`Nivel: ${fLevel}`);
    if (fGrade) subtitleParts.push(`Grado: ${fGrade}`);
    if (fGroup) subtitleParts.push(`Grupo: ${fGroup}`);
    if (query) subtitleParts.push(`Búsqueda: "${query}"`);
    doc.setFontSize(14); doc.setTextColor(11, 42, 91);
    doc.text(`${school} — ${isTeacher ? "Docentes y Personal" : "Alumnos"}`, 14, 14);
    doc.setFontSize(9); doc.setTextColor(90);
    doc.text([`${filteredItems.length} registro(s)` + (subtitleParts.length ? " · " + subtitleParts.join(" · ") : ""), new Date().toLocaleString("es-MX")].join("   |   "), 14, 20);
    const head = isTeacher ? [["#", "Nombre", "Cargo/Materia", "UID NFC"]] : [["#", "Nombre", "Grado", "Grupo", "Nivel", "UID NFC", "Tutor"]];
    const body = filteredItems.map((s, i) => {
      const { grupo } = parseGradeGroup(s.grade);
      return isTeacher
        ? [i + 1, s.full_name || "", s.grade || "", s.nfc_uid || ""]
        : [i + 1, s.full_name || "", s.grade || "—", grupo || "—", s.level || "—", s.nfc_uid || "—", primaryTutorLabel(s) || "—"];
    });
    autoTable(doc, {
      head, body, startY: 24, styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [11, 42, 91], textColor: 255 },
      alternateRowStyles: { fillColor: [244, 246, 249] },
      margin: { left: 14, right: 14 },
    });
    doc.save(`${isTeacher ? "personal" : "alumnos"}${filterSuffix()}.pdf`);
    toast.success(`${filteredItems.length} registros exportados a PDF`);
  };


  const uidConflicts = useMemo(() => {
    const map = {};
    for (const s of items) {
      const u = (s.nfc_uid || "").trim();
      if (!u) continue;
      (map[u] = map[u] || []).push(s);
    }
    return Object.entries(map).filter(([, arr]) => arr.length > 1).map(([uid, members]) => ({ uid, members }));
  }, [items]);

  const saveEditUid = async () => {
    if (!editItem) return;
    try {
      await api.put(`/students/${editItem.id}`, { nfc_uid: editUid, turno: editTurno || null, ...(isTeacher ? { jornada: editJornada || null } : {}) });
      if (!isTeacher) {
        const b = editItem.billing || {};
        const sch = scholarships.find((s) => s.id === editSchId);
        await api.put(`/students/${editItem.id}/billing`, {
          tutors: b.tutors || [],
          address: b.address || "",
          plan: editPlan,
          monthly_fee: Number(editMonthly) || 0,
          enrollment_fee: Number(editEnroll) || 0,
          scholarship_pct: sch ? Number(sch.pct) || 0 : 0,
          scholarship_name: sch ? sch.name : "",
        });
      }
      toast.success("Datos actualizados");
      setEditItem(null); setEditUid("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid={isTeacher ? "teachers-page" : "students-page"}>
      <BajaDialog student={bajaItem} onClose={() => setBajaItem(null)} onDone={load} />
      <div className="mb-10 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2">
            <span className="w-8 h-px bg-navy" /> {labels.section}
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy">{labels.title}</h1>
          {quota && quota.limit != null && (
            <div className="mt-3 inline-flex items-center gap-2" data-testid="registration-quota-badge">
              <span className={`text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 rounded ${quota.remaining === 0 ? "bg-red-100 text-red-700" : "bg-navy/5 text-navy"}`}>
                {(quota.used).toLocaleString()} / {(quota.limit).toLocaleString()} registros
              </span>
              {quota.remaining === 0 && <span className="text-[11px] text-red-600">Límite alcanzado</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={importInput}
            type="file"
            accept=".csv,.xlsx,.xlsm,.txt"
            className="hidden"
            data-testid={tid("import-input")}
            onChange={(e) => onImport(e.target.files?.[0])}
          />
          <button
            data-testid={tid("import-button")}
            onClick={() => importInput.current?.click()}
            disabled={importing}
            className="inline-flex items-center gap-2 border border-navy/20 text-navy hover:border-navy hover:bg-nardo-50 px-4 py-2.5 rounded-md transition-colors text-sm font-medium disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {importing ? "Importando…" : "Importar"}
          </button>
          <button
            data-testid={tid("add-button")}
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> {labels.add}
          </button>
        </div>
      </div>

      <div className="mb-4 text-xs text-nardo-500">
        ¿Importas por primera vez?{" "}
        <button data-testid={tid("template-download")} onClick={downloadTemplate} className="text-navy underline hover:text-navy-light">
          Descarga la plantilla CSV
        </button>{" "}
        (columnas: Nombre{isTeacher ? ", Cargo" : ", Grado, Nivel"}, UID NFC). Acepta .csv y .xlsx.
      </div>

      {uidConflicts.length > 0 && (
        <div className="mb-4 bg-orange-50 border border-orange-200 rounded-md p-4" data-testid="uid-conflict-banner">
          <div className="flex items-center gap-2 text-orange-700 font-medium text-sm mb-2">
            <AlertTriangle className="w-4 h-4" /> UID repetido detectado
          </div>
          <p className="text-xs text-orange-700/80 mb-3">Dos o más {isTeacher ? "docentes" : "alumnos"} comparten el mismo UID. El acceso de padres por UID requiere que sea único. Corrígelo dando clic en el nombre:</p>
          <div className="space-y-2">
            {uidConflicts.map((c) => (
              <div key={c.uid} className="bg-white rounded border border-orange-200 p-3" data-testid="uid-conflict-group">
                <div className="text-xs font-mono text-navy mb-2">UID: <strong>{c.uid}</strong></div>
                <div className="flex flex-wrap gap-2">
                  {c.members.map((m) => (
                    <button key={m.id} onClick={() => { setEditItem(m); setEditUid(m.nfc_uid || ""); }} data-testid={`uid-conflict-fix-${m.id}`}
                      className="inline-flex items-center gap-1.5 text-xs border border-nardo-300 hover:border-navy text-navy px-3 py-1.5 rounded-md transition-colors">
                      <Pencil className="w-3 h-3" /> {m.full_name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-nardo-200 rounded-md mb-4 px-4 py-3 flex items-center gap-3" data-testid={tid("search-bar")}>
        <Search className="w-4 h-4 text-nardo-500 shrink-0" />
        <input
          data-testid={tid("search-input")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={isTeacher ? "Buscar por nombre, materia o UID…" : "Buscar por nombre, grado, grupo, nivel o UID… (ej. 1º A Preescolar)"}
          className="flex-1 bg-transparent outline-none text-sm text-navy placeholder:text-nardo-400"
        />
        {query && (
          <button onClick={() => setQuery("")} className="text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy" data-testid={tid("search-clear")}>Limpiar</button>
        )}
        <span className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-mono">{filteredItems.length}/{items.length}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={tid("filters-bar")}>
        {!isTeacher && (
          <>
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-nardo-500"><ListFilter className="w-3.5 h-3.5" /> Filtros</span>
            <select value={fLevel} onChange={(e) => setFLevel(e.target.value)} data-testid={tid("filter-level")}
              className="text-sm border border-nardo-200 rounded-md px-2.5 py-1.5 bg-white text-navy outline-none focus:border-navy">
              <option value="">Nivel: todos</option>
              {filterOptions.levels.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
            </select>
            <select value={fGrade} onChange={(e) => setFGrade(e.target.value)} data-testid={tid("filter-grade")}
              className="text-sm border border-nardo-200 rounded-md px-2.5 py-1.5 bg-white text-navy outline-none focus:border-navy">
              <option value="">Grado: todos</option>
              {filterOptions.grades.map((g) => <option key={g} value={g}>{g}°</option>)}
            </select>
            <select value={fGroup} onChange={(e) => setFGroup(e.target.value)} data-testid={tid("filter-group")}
              className="text-sm border border-nardo-200 rounded-md px-2.5 py-1.5 bg-white text-navy outline-none focus:border-navy">
              <option value="">Grupo: todos</option>
              {filterOptions.groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            {anyFilter && (
              <button onClick={clearAll} data-testid={tid("filter-clear")} className="text-xs text-nardo-500 hover:text-navy underline">Limpiar filtros</button>
            )}
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-nardo-500 hidden sm:inline">Exportar {filteredItems.length}:</span>
          <button onClick={exportCSV} data-testid={tid("export-excel")} title="Exportar a Excel (CSV)"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-300 rounded-md px-3 py-1.5 hover:bg-emerald-50">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportPDF} data-testid={tid("export-pdf")} title="Exportar a PDF"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 border border-red-300 rounded-md px-3 py-1.5 hover:bg-red-50">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-nardo-200 bg-nardo-50/50 text-[10px] uppercase tracking-[0.25em] text-nardo-500">
          <div className="col-span-5">{isTeacher ? "Docente" : "Alumno"}</div>
          <div className="col-span-2 hidden md:block">{labels.gradeLabel}</div>
          <div className="col-span-3 hidden md:block">UID NFC</div>
          <div className="col-span-7 md:col-span-2 text-right">Acciones</div>
        </div>
        {filteredItems.length === 0 && (
          <div className="px-6 py-16 text-center text-nardo-500 text-sm">{anyFilter ? "Sin coincidencias para tu búsqueda o filtros." : labels.empty}</div>
        )}
        {filteredItems.map((s) => (
          <div key={s.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-nardo-100 items-center hover:bg-nardo-50/40 transition-colors" data-testid={tid("list-item")}>
            <div
              className="col-span-5 flex items-center gap-3 cursor-pointer group"
              onClick={() => navigate(`${isTeacher ? "/teachers" : "/students"}/${s.id}`)}
              data-testid={tid(`open-${s.id}`)}
            >
              <PersonPhoto person={s} />
              <div>
                <div className="font-medium text-navy group-hover:underline">{s.full_name}</div>
                {!isTeacher && primaryTutorLabel(s) && (
                  <div className="text-[11px] text-nardo-500" data-testid={tid(`tutor-label-${s.id}`)}>Tutor: {primaryTutorLabel(s)}</div>
                )}
                <div className="md:hidden text-xs text-nardo-500">{s.grade} · <span className="font-mono">{s.nfc_uid || "Sin UID"}</span></div>
              </div>
            </div>
            <div className="col-span-2 hidden md:block text-sm text-nardo-600">
              {s.grade || "—"}
              {!isTeacher && s.level && <div className="text-[11px] text-nardo-400">{s.level}</div>}
            </div>
            <div className="col-span-3 hidden md:block text-xs font-mono text-nardo-600">{s.nfc_uid || <span className="text-nardo-400">Sin UID</span>}</div>
            <div className="col-span-7 md:col-span-2 flex justify-end gap-2">
              <input
                ref={(el) => { fileInputs.current[s.id] = el; }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPhoto(s.id, e.target.files?.[0])}
              />
              <button title="Editar (UID, turno, plan y beca)" data-testid={tid(`edit-${s.id}`)} onClick={() => { setEditItem(s); setEditUid(s.nfc_uid || ""); }} className="p-2 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded-md transition-colors">
                <Pencil className="w-4 h-4" />
              </button>
              <button title="Subir foto" data-testid={tid(`upload-${s.id}`)} onClick={() => fileInputs.current[s.id]?.click()} className="p-2 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded-md transition-colors">
                <Camera className="w-4 h-4" />
              </button>
              <button title="Copiar link de programación" data-testid={tid(`link-${s.id}`)} onClick={() => copyLink(s.program_token)} className="p-2 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded-md transition-colors">
                <Link2 className="w-4 h-4" />
              </button>
              {!isTeacher && (
                <button title="Expediente y cobranza" data-testid={tid(`expediente-${s.id}`)} onClick={() => navigate(`/students/${s.id}/expediente`)} className="p-2 text-nardo-500 hover:text-emerald-600 hover:bg-nardo-50 rounded-md transition-colors">
                  <Wallet className="w-4 h-4" />
                </button>
              )}
              {!isTeacher && (
                <button title="Dar de baja" data-testid={tid(`baja-${s.id}`)} onClick={() => setBajaItem(s)} className="p-2 text-nardo-500 hover:text-orange-600 hover:bg-nardo-50 rounded-md transition-colors">
                  <UserMinus className="w-4 h-4" />
                </button>
              )}
              {!isStaff && (
                <button title="Eliminar" data-testid={tid(`delete-${s.id}`)} onClick={() => remove(s.id)} className="p-2 text-nardo-500 hover:text-destructive hover:bg-nardo-50 rounded-md transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white border border-nardo-200 rounded-md w-full max-w-lg my-auto max-h-[90vh] overflow-y-auto p-8 relative animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-nardo-500 hover:text-navy" data-testid={tid("close-modal")}><X className="w-4 h-4" /></button>
            <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
            <h2 className="font-display font-bold text-2xl text-navy mb-1">{labels.newTitle}</h2>
            <p className="text-sm text-nardo-500 mb-6">Podrás asignar foto y UID luego.</p>
            <form onSubmit={create} className="space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nombre completo</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required data-testid={tid("name-input")}
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy" placeholder={isTeacher ? "Ej. Prof. Juan Pérez" : "Ej. María González"} />
              </div>
              {!isTeacher && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nivel de estudios</label>
                  <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid={tid("level-select")}
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                    <option value="">Selecciona un nivel…</option>
                    {LEVELS.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">{labels.gradeLabel}</label>
                  <input value={grade} onChange={(e) => setGrade(e.target.value)} data-testid={tid("grade-input")}
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none" placeholder={labels.gradePh} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">UID NFC (opcional)</label>
                  <input value={uid} onChange={(e) => setUid(e.target.value)} data-testid={tid("uid-input")}
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none font-mono" placeholder="04 A1 9F 22" />
                </div>
              </div>
              {shifts.length > 0 && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Turno</label>
                  <select value={turno} onChange={(e) => setTurno(e.target.value)} data-testid={tid("turno-select")}
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                    <option value="">Predeterminado (horario general)</option>
                    {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <p className="text-[11px] text-nardo-400 mt-1">Define el horario con el que se calculan sus retardos y salidas anticipadas.</p>
                </div>
              )}
              {isTeacher && jornadas.length > 0 && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Jornada de movimientos</label>
                  <select value={jornada} onChange={(e) => setJornada(e.target.value)} data-testid={tid("jornada-select")}
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                    <option value="">Sin jornada (solo entrada/salida)</option>
                    {jornadas.map((j) => <option key={j.id} value={j.id}>{`${j.name} · ${j.movements.length} mov.`}</option>)}
                  </select>
                  <p className="text-[11px] text-nardo-400 mt-1">Determina los movimientos esperados del día (entrada, comida, desayuno, salida).</p>
                </div>
              )}
              {!isTeacher && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Tipo de sangre (opcional)</label>
                  <select value={blood} onChange={(e) => setBlood(e.target.value)} data-testid={tid("blood-select")}
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                    <option value="">Sin especificar</option>
                    {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <p className="text-[11px] text-nardo-400 mt-1">Aparecerá automáticamente en la credencial del alumno.</p>
                </div>
              )}
              {!isTeacher && (
                <div className="space-y-3 border border-nardo-200 rounded-md p-3" data-testid="alta-billing">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 font-semibold">Plan de pago y colegiatura</label>
                  {hasPlans && (
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Plan de pago</label>
                      <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid={tid("plan-select")}
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                        <option value="">Sin plan (colegiatura manual)</option>
                        {PLAN_KEYS.map((k) => { const amt = planAmount(user, level, k, grade); return (
                          <option key={k} value={k}>{PLAN_LABELS[k]}{amt > 0 ? ` · $${amt.toLocaleString("es-MX")}/mes` : ""}</option>
                        ); })}
                      </select>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Colegiatura</label>
                      <input type="number" min="0" step="0.01" data-testid={tid("monthly-fee")}
                        value={plan ? (planAmount(user, level, plan, grade) || "") : monthlyFee} disabled={!!plan}
                        onChange={(e) => setMonthlyFee(e.target.value)}
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy disabled:text-nardo-400" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Inscripción</label>
                      <input type="number" min="0" step="0.01" data-testid={tid("enrollment-fee")}
                        value={enrollment} onChange={(e) => setEnrollment(e.target.value)}
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Beca</label>
                      <select value={schId} onChange={(e) => setSchId(e.target.value)} data-testid={tid("scholarship-select")}
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                        <option value="">Sin beca</option>
                        {scholarships.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.pct}%)</option>)}
                      </select>
                    </div>
                  </div>
                  {(() => {
                    const sch = scholarships.find((s) => s.id === schId);
                    const base = plan ? (planAmount(user, level, plan, grade) || 0) : (Number(monthlyFee) || 0);
                    const pct = sch ? Number(sch.pct) || 0 : 0;
                    if (!(pct > 0 && base > 0)) return null;
                    return (
                      <p className="text-[12px] text-emerald-700 font-medium" data-testid="alta-effective-fee">
                        Colegiatura con beca ({pct}%): ${(base - base * pct / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / mes
                      </p>
                    );
                  })()}
                  <p className="text-[11px] text-nardo-400">
                    {plan ? "La colegiatura se toma del plan según el nivel del alumno. " : ""}
                    {scholarships.length === 0 ? "Configura las becas en Ajustes → Cobranza y pagos para elegirlas aquí." : "Al elegir una beca, su porcentaje se aplica automáticamente sobre la colegiatura."}
                  </p>
                </div>
              )}
              {!isTeacher && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Tutores (WhatsApp / correo para avisos)</label>
                    <button type="button" onClick={addTutor} data-testid="add-tutor-row" className="text-xs text-navy hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar tutor</button>
                  </div>
                  {tutors.map((t, i) => (
                    <div key={i} className="border border-nardo-200 rounded-md p-3 space-y-2" data-testid={`tutor-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <input value={t.name} onChange={(e) => updateTutor(i, "name", e.target.value)} placeholder="Nombre del tutor" data-testid={`tutor-name-${i}`}
                          className="flex-1 border-b border-nardo-200 bg-transparent py-1.5 text-sm focus:border-navy outline-none" />
                        <select value={t.relation} onChange={(e) => updateTutor(i, "relation", e.target.value)} data-testid={`tutor-relation-${i}`}
                          className="border-b border-nardo-200 bg-transparent py-1.5 text-sm focus:border-navy outline-none text-navy">
                          <option value="">Parentesco…</option>
                          {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        {tutors.length > 1 && <button type="button" onClick={() => removeTutor(i)} data-testid={`tutor-remove-${i}`} className="text-nardo-400 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={t.whatsapp} onChange={(e) => updateTutor(i, "whatsapp", e.target.value)} placeholder="WhatsApp (con lada)" inputMode="tel" data-testid={`tutor-whatsapp-${i}`}
                          className="border-b border-nardo-200 bg-transparent py-1.5 text-sm font-mono focus:border-navy outline-none" />
                        <input value={t.email} onChange={(e) => updateTutor(i, "email", e.target.value)} placeholder="Correo" type="email" data-testid={`tutor-email-${i}`}
                          className="border-b border-nardo-200 bg-transparent py-1.5 text-sm focus:border-navy outline-none" />
                      </div>
                    </div>
                  ))}
                  <p className="text-[11px] text-nardo-400">A estos contactos se les enviarán recordatorios de pago y comprobantes.</p>
                </div>
              )}
              <button type="submit" disabled={loading} data-testid={tid("submit-button")}
                className="w-full bg-navy hover:bg-navy-light text-white py-3 rounded-md transition-colors font-medium disabled:opacity-50">
                {loading ? "Guardando…" : (isTeacher ? "Crear docente" : "Crear alumno")}
              </button>
            </form>
          </div>
        </div>
      )}
      {editItem && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditItem(null)}>
          <div className="bg-white border border-nardo-200 rounded-md w-full max-w-lg p-8 relative animate-slide-up my-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEditItem(null)} className="absolute top-4 right-4 text-nardo-500 hover:text-navy" data-testid="edit-uid-close"><X className="w-4 h-4" /></button>
            <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
            <h2 className="font-display font-bold text-xl text-navy mb-1">Editar persona</h2>
            <p className="text-sm text-nardo-500 mb-6">{editItem.full_name}</p>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">UID NFC</label>
            <input value={editUid} onChange={(e) => setEditUid(e.target.value)} data-testid="edit-uid-input" autoFocus
              className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none font-mono" placeholder="04 A1 9F 22" />
            {shifts.length > 0 && (
              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Turno</label>
                <select value={editTurno} onChange={(e) => setEditTurno(e.target.value)} data-testid="edit-turno-select"
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                  <option value="">Predeterminado (horario general)</option>
                  {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            {isTeacher && jornadas.length > 0 && (
              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Jornada de movimientos</label>
                <select value={editJornada} onChange={(e) => setEditJornada(e.target.value)} data-testid="edit-jornada-select"
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                  <option value="">Sin jornada (solo entrada/salida)</option>
                  {jornadas.map((j) => <option key={j.id} value={j.id}>{`${j.name} · ${j.movements.length} mov.`}</option>)}
                </select>
              </div>
            )}
            {!isTeacher && (
              <div className="mt-4 space-y-3 border border-nardo-200 rounded-md p-3" data-testid="edit-billing">
                <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 font-semibold">Plan de pago y colegiatura</label>
                {hasPlans && (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Plan de pago</label>
                    <select value={editPlan} onChange={(e) => setEditPlan(e.target.value)} data-testid="edit-plan-select"
                      className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                      <option value="">Sin plan (colegiatura manual)</option>
                      {PLAN_KEYS.map((k) => { const amt = planAmount(user, editItem.level, k, editItem.grade); return (
                        <option key={k} value={k}>{PLAN_LABELS[k]}{amt > 0 ? ` · $${amt.toLocaleString("es-MX")}/mes` : ""}</option>
                      ); })}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Colegiatura</label>
                    <input type="number" min="0" step="0.01" data-testid="edit-monthly-fee"
                      value={editPlan ? (planAmount(user, editItem.level, editPlan, editItem.grade) || "") : editMonthly} disabled={!!editPlan}
                      onChange={(e) => setEditMonthly(e.target.value)}
                      className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy disabled:text-nardo-400" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Inscripción</label>
                    <input type="number" min="0" step="0.01" data-testid="edit-enrollment-fee"
                      value={editEnroll} onChange={(e) => setEditEnroll(e.target.value)}
                      className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Beca</label>
                    <select value={editSchId} onChange={(e) => setEditSchId(e.target.value)} data-testid="edit-scholarship-select"
                      className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                      <option value="">Sin beca</option>
                      {scholarships.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.pct}%)</option>)}
                    </select>
                  </div>
                </div>
                {(() => {
                  const sch = scholarships.find((s) => s.id === editSchId);
                  const base = editPlan ? (planAmount(user, editItem.level, editPlan, editItem.grade) || 0) : (Number(editMonthly) || 0);
                  const pct = sch ? Number(sch.pct) || 0 : 0;
                  if (!(pct > 0 && base > 0)) return null;
                  return (
                    <p className="text-[12px] text-emerald-700 font-medium" data-testid="edit-effective-fee">
                      Colegiatura con beca ({pct}%): ${(base - base * pct / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / mes
                    </p>
                  );
                })()}
                <p className="text-[11px] text-nardo-400">
                  {editPlan ? "La colegiatura se toma del plan según el nivel y grado del alumno. " : ""}
                  {scholarships.length === 0 ? "Configura las becas en Ajustes → Cobranza y pagos para elegirlas aquí." : "Al elegir una beca, su porcentaje se aplica automáticamente sobre la colegiatura."}
                </p>
              </div>
            )}
            <button onClick={saveEditUid} data-testid="edit-uid-save"
              className="mt-6 w-full bg-navy hover:bg-navy-light text-white py-3 rounded-md transition-colors font-medium">Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}
